import React, { useEffect, useState } from 'react';
import { Box, Tabs, Text } from '@mantine/core';
import { JsonDiffViewer } from '../common/JsonDiffViewer';
import { JsonViewer } from '../common/JsonViewer';
import { TextDiffViewer } from '../common/TextDiffViewer';
import { SharedDebugComponent, type SharedDebugTabKey } from '../common/SharedDebugComponent';
import type { EntityEntitlementForTargetResponse } from '../../types/EntityEntitlements';

export interface UnifiedRawViewsTabsProps {
    entityLabel: string;
    expectedPayload: any;
    formData: Record<string, any>;
    originalDataForDiff: Record<string, any> | null;
    currentDataForDiff: Record<string, any> | null;
    debugEntity: any;
    entityType?: string;
    targetEntitlement?: EntityEntitlementForTargetResponse | null;
    isEmailTemplate?: boolean;
    isEditMode?: boolean;
}

const LEADING_DEBUG_TABS: Array<{ value: SharedDebugTabKey; label: string }> = [
    { value: 'overview', label: 'Overview' },
    { value: 'drill', label: 'Drill' }
];

const TRAILING_DEBUG_TABS: Array<{ value: SharedDebugTabKey; label: string }> = [
    { value: 'itemized', label: 'Itemized' }
];

export const UnifiedRawViewsTabs: React.FC<UnifiedRawViewsTabsProps> = ({
    entityLabel,
    expectedPayload,
    formData,
    originalDataForDiff,
    currentDataForDiff,
    debugEntity,
    entityType,
    targetEntitlement,
    isEmailTemplate = false,
    isEditMode = false
}) => {
    const [activeTab, setActiveTab] = useState<string | null>('overview');

    useEffect(() => {
        if (!isEditMode && (activeTab === 'jsonDiff' || activeTab === 'expectedPayload')) {
            setActiveTab('overview');
        }
    }, [activeTab, isEditMode]);

    return (
        <Tabs value={activeTab} onChange={setActiveTab} mb="md" keepMounted={false}>
            <Tabs.List>
                {LEADING_DEBUG_TABS.map((tab) => (
                    <Tabs.Tab key={tab.value} value={tab.value}>{tab.label}</Tabs.Tab>
                ))}
                <Tabs.Tab value="jsonDiff" disabled={!isEditMode}>JSON Diff</Tabs.Tab>
                <Tabs.Tab value="expectedPayload" disabled={!isEditMode}>Expected Payload</Tabs.Tab>
                <Tabs.Tab value="formData">FormData</Tabs.Tab>
                {TRAILING_DEBUG_TABS.map((tab) => (
                    <Tabs.Tab key={tab.value} value={tab.value}>{tab.label}</Tabs.Tab>
                ))}
                <Tabs.Tab value="rawJson">Raw JSON</Tabs.Tab>
            </Tabs.List>

            {[...LEADING_DEBUG_TABS, ...TRAILING_DEBUG_TABS].map((tab) => (
                <Tabs.Panel key={tab.value} value={tab.value}>
                    <SharedDebugComponent
                        entity={debugEntity}
                        entityType={entityType}
                        targetEntitlement={targetEntitlement ?? null}
                        forcedTab={tab.value}
                        bare
                    />
                </Tabs.Panel>
            ))}

            <Tabs.Panel value="jsonDiff">
                <Box>
                    {originalDataForDiff && currentDataForDiff ? (
                        (() => {
                            const hasBodyProperty = 'Body' in originalDataForDiff || 'body' in originalDataForDiff ||
                                'Body' in currentDataForDiff || 'body' in currentDataForDiff;
                            if (isEmailTemplate && hasBodyProperty) {
                                const bodyKey = 'Body' in originalDataForDiff || 'Body' in currentDataForDiff ? 'Body' : 'body';
                                const originalWithoutBody = { ...originalDataForDiff };
                                const currentWithoutBody = { ...currentDataForDiff };
                                const originalBody = originalWithoutBody[bodyKey];
                                const currentBody = currentWithoutBody[bodyKey];
                                delete originalWithoutBody[bodyKey];
                                delete currentWithoutBody[bodyKey];
                                return (
                                    <Box>
                                        <JsonDiffViewer
                                            originalData={originalWithoutBody}
                                            currentData={currentWithoutBody}
                                            title={`${entityLabel} Changes (excluding Body)`}
                                            excludedPaths={['Id', 'EntityType', 'Body', 'body']}
                                            collapsible={false}
                                            defaultExpanded={true}
                                        />
                                        <Box mt="lg">
                                            <TextDiffViewer
                                                originalText={originalBody || ''}
                                                currentText={currentBody || ''}
                                                title="Body Property Changes"
                                                language="html"
                                            />
                                        </Box>
                                    </Box>
                                );
                            }
                            return (
                                <JsonDiffViewer
                                    originalData={originalDataForDiff}
                                    currentData={currentDataForDiff}
                                    title={`${entityLabel} Changes`}
                                    excludedPaths={['Id', 'EntityType']}
                                    collapsible={false}
                                    defaultExpanded={true}
                                />
                            );
                        })()
                    ) : (
                        <Box p="md">
                            <Text size="sm" c="dimmed">No changes detected</Text>
                        </Box>
                    )}
                </Box>
            </Tabs.Panel>

            <Tabs.Panel value="expectedPayload">
                <JsonViewer
                    data={expectedPayload}
                    title="Expected PATCH Payload"
                    description={`This is the PATCH request that will be sent to update the ${entityLabel.toLowerCase()}.`}
                    formatBodyMultiline
                />
            </Tabs.Panel>

            <Tabs.Panel value="formData">
                <JsonViewer
                    data={formData}
                    title="FormData"
                    description="Raw form state (keys and values as stored in the dialog)."
                    formatBodyMultiline
                />
            </Tabs.Panel>

            <Tabs.Panel value="rawJson">
                <SharedDebugComponent
                    entity={debugEntity}
                    entityType={entityType}
                    targetEntitlement={targetEntitlement ?? null}
                    forcedTab="rawJson"
                    bare
                />
            </Tabs.Panel>
        </Tabs>
    );
};
